import { useState, useRef, useEffect, useCallback } from "react";
import { io } from "socket.io-client";
import api from "../services/api";
import { callService } from "../services/callService";

const DEFAULT_ICE_SERVERS = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

export default function useWebRTC(user) {
  const [callStatus, setCallStatus] = useState("idle");
  const [remoteStream, setRemoteStream] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [callerInfo, setCallerInfo] = useState(null);
  const [calleeDisplayName, setCalleeDisplayName] = useState(null);
  const [currentCallId, setCurrentCallId] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);

  const socketRef = useRef(null);
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const currentCallIdRef = useRef(null);
  const otherUserIdRef = useRef(null);
  const iceServersRef = useRef(DEFAULT_ICE_SERVERS.iceServers);
  const disconnectTimeoutRef = useRef(null);

  useEffect(() => {
    currentCallIdRef.current = currentCallId;
  }, [currentCallId]);

  const startDurationTimer = useCallback(() => {
    setCallDuration(0);
    durationIntervalRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const cleanupCall = useCallback(() => {
    stopDurationTimer();
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
    if (peerRef.current) {
      peerRef.current.close();
      peerRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    setLocalStream(null);
    setRemoteStream(null);
    setIsMuted(false);
    setCallDuration(0);
    setCalleeDisplayName(null);
  }, [stopDurationTimer]);

  const getUserMediaStream = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      setLocalStream(stream);
      return stream;
    } catch (err) {
      setError(
        err.name === "NotAllowedError"
          ? "Microphone permission denied. Please allow microphone access."
          : "Could not access microphone."
      );
      throw err;
    }
  }, []);

  const createPeerConnection = useCallback((stream) => {
    const pc = new RTCPeerConnection({
      iceServers: iceServersRef.current,
    });

    stream.getTracks().forEach((track) => {
      pc.addTrack(track, stream);
    });

    pc.onicecandidate = (e) => {
      if (e.candidate && socketRef.current?.connected) {
        socketRef.current.emit("call:signal", {
          callId: currentCallIdRef.current,
          to: otherUserIdRef.current,
          signal: { type: "candidate", candidate: e.candidate },
        });
      }
    };

    pc.ontrack = (e) => {
      setRemoteStream(e.streams[0]);
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === "connected") {
        if (disconnectTimeoutRef.current) {
          clearTimeout(disconnectTimeoutRef.current);
          disconnectTimeoutRef.current = null;
        }
      } else if (
        pc.iceConnectionState === "disconnected" ||
        pc.iceConnectionState === "failed"
      ) {
        setError("Connection lost");
        if (!disconnectTimeoutRef.current) {
          disconnectTimeoutRef.current = setTimeout(async () => {
            disconnectTimeoutRef.current = null;
            if (currentCallIdRef.current && socketRef.current?.connected) {
              try {
                await callService.endCall(currentCallIdRef.current);
              } catch {
                // ignore
              }
            }
            handlersRef.current.stopDurationTimer();
            handlersRef.current.cleanupCall();
            setCallStatus("ended");
            setTimeout(() => setCallStatus("idle"), 2000);
          }, 3000);
        }
      }
    };

    peerRef.current = pc;
    return pc;
  }, []);

  const handleSignalOffer = useCallback(
    async (signalPayload) => {
      const stream = await getUserMediaStream();
      const pc = createPeerConnection(stream);
      await pc.setRemoteDescription(
        new RTCSessionDescription(signalPayload.offer)
      );
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socketRef.current?.emit("call:signal", {
        callId: currentCallIdRef.current,
        to: otherUserIdRef.current,
        signal: { type: "answer", answer },
      });
    },
    [getUserMediaStream, createPeerConnection]
  );

  const handleSignalAnswer = useCallback(async (signalPayload) => {
    if (peerRef.current && signalPayload.answer) {
      await peerRef.current.setRemoteDescription(
        new RTCSessionDescription(signalPayload.answer)
      );
    }
  }, []);

  const handleSignalCandidate = useCallback(async (signalPayload) => {
    if (peerRef.current && signalPayload.candidate) {
      try {
        await peerRef.current.addIceCandidate(
          new RTCIceCandidate(signalPayload.candidate)
        );
      } catch (err) {
        console.error("Error adding ICE candidate:", err);
      }
    }
  }, []);

  const handlersRef = useRef({
    startDurationTimer,
    stopDurationTimer,
    cleanupCall,
    handleSignalOffer,
    handleSignalAnswer,
    handleSignalCandidate,
  });
  handlersRef.current = {
    startDurationTimer,
    stopDurationTimer,
    cleanupCall,
    handleSignalOffer,
    handleSignalAnswer,
    handleSignalCandidate,
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !user) return;

    const baseURL = api.defaults.baseURL || "";
    const origin = baseURL.replace(/\/?api\/?$/i, "") || baseURL;

    const socket = io(origin, {
      auth: { token },
      transports: ["websocket", "polling"],
    });

    socket.on("call:incoming", (data) => {
      setCallStatus("ringing");
      setCallerInfo(data.caller);
      setCurrentCallId(data.callId);
      otherUserIdRef.current = data.caller?.id || data.caller?._id;
      if (data.iceServers) {
        iceServersRef.current = data.iceServers;
      }
    });

    socket.on("call:accepted", (data) => {
      setCallStatus("ongoing");
      if (data?.iceServers) {
        iceServersRef.current = data.iceServers;
      }
      handlersRef.current.startDurationTimer();
    });

    socket.on("call:rejected", (data) => {
      setCallStatus("idle");
      setError(data.reason || "Call was rejected");
      handlersRef.current.cleanupCall();
    });

    socket.on("call:ended", (data) => {
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
        disconnectTimeoutRef.current = null;
      }
      setCallStatus("ended");
      handlersRef.current.stopDurationTimer();
      setTimeout(() => {
        setCallStatus("idle");
        handlersRef.current.cleanupCall();
      }, 2000);
    });

    socket.on("call:missed", () => {
      setCallStatus("idle");
      setError("Call was not answered");
      handlersRef.current.cleanupCall();
    });

    socket.on("call:signal", async (data) => {
      try {
        const signal = data.signal;
        if (!signal) return;
        if (signal.type === "offer") {
          await handlersRef.current.handleSignalOffer(signal);
        } else if (signal.type === "answer") {
          await handlersRef.current.handleSignalAnswer(signal);
        } else if (signal.type === "candidate") {
          await handlersRef.current.handleSignalCandidate(signal);
        }
      } catch (err) {
        setError("Connection error during signaling");
      }
    });

    socketRef.current = socket;

    return () => {
      handlersRef.current.cleanupCall();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user]);

  const initiateCall = useCallback(
    async (calleeId, binId, displayName) => {
      setError(null);
      setCallStatus("requesting");
      setCalleeDisplayName(displayName || null);
      setCallerInfo(null);
      try {
        const result = await callService.initiateCall(calleeId, binId);
        const callId =
          result.data?.callId || result.call?._id || result.data?.call?._id || result._id;
        setCurrentCallId(callId);
        otherUserIdRef.current = calleeId;

        if (result.iceServers) {
          iceServersRef.current = result.iceServers;
        } else if (result.data?.iceServers) {
          iceServersRef.current = result.data.iceServers;
        }

        const stream = await getUserMediaStream();
        const pc = createPeerConnection(stream);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        socketRef.current?.emit("call:signal", {
          callId,
          to: calleeId,
          signal: { type: "offer", offer },
        });
      } catch (err) {
        setCallStatus("idle");
        setError(err.response?.data?.message || "Failed to initiate call");
        cleanupCall();
      }
    },
    [getUserMediaStream, createPeerConnection, cleanupCall]
  );

  const acceptCall = useCallback(async () => {
    if (!currentCallIdRef.current) return;
    setError(null);
    try {
      const result = await callService.acceptCall(currentCallIdRef.current);
      if (result?.iceServers) {
        iceServersRef.current = result.iceServers;
      } else if (result?.data?.iceServers) {
        iceServersRef.current = result.data.iceServers;
      }
      setCallStatus("ongoing");
      startDurationTimer();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to accept call");
    }
  }, [startDurationTimer]);

  const rejectCall = useCallback(async () => {
    if (!currentCallIdRef.current) return;
    try {
      await callService.rejectCall(currentCallIdRef.current);
    } catch {
      // ignore
    }
    setCallStatus("idle");
    setCallerInfo(null);
    setCurrentCallId(null);
    cleanupCall();
  }, [cleanupCall]);

  const endCall = useCallback(async () => {
    if (currentCallIdRef.current) {
      try {
        await callService.endCall(currentCallIdRef.current);
      } catch {
        // ignore
      }
    }
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
    setCallStatus("ended");
    stopDurationTimer();
    setTimeout(() => {
      setCallStatus("idle");
      setCallerInfo(null);
      setCurrentCallId(null);
      cleanupCall();
    }, 1000);
  }, [stopDurationTimer, cleanupCall]);

  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  }, []);

  return {
    callStatus,
    remoteStream,
    localStream,
    callerInfo,
    calleeDisplayName,
    currentCallId,
    isMuted,
    callDuration,
    error,
    initiateCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    clearError: () => setError(null),
  };
}
